<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainAssistants/getUIFlags'),
};

const initialState = {
  name: '',
  description: '',
  productName: '',
  welcomeMessage: '',
  handoffMessage: '',
  outOfOfficeMessage: '',
  resolutionMessage: '',
  instructions: '',
  triggerKeywords: [],
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
  },
  enableHandoff: true,
  handoffDisabledResponse: '',
  temperature: 1,
};

const state = reactive({ ...initialState });

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
  welcomeMessage: { minLength: minLength(1) },
  handoffMessage: { minLength: minLength(1) },
  outOfOfficeMessage: { minLength: minLength(1) },
  resolutionMessage: { minLength: minLength(1) },
  instructions: { minLength: minLength(1) },
  handoffDisabledResponse: {},
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  description: getErrorMessage('description'),
  productName: getErrorMessage('productName'),
  welcomeMessage: getErrorMessage('welcomeMessage'),
  handoffMessage: getErrorMessage('handoffMessage'),
  outOfOfficeMessage: getErrorMessage('outOfOfficeMessage'),
  resolutionMessage: getErrorMessage('resolutionMessage'),
  instructions: getErrorMessage('instructions'),
  handoffDisabledResponse: getErrorMessage('handoffDisabledResponse'),
}));

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.name = assistant.name;
  state.description = assistant.description;
  state.productName = config.product_name;
  state.welcomeMessage = config.welcome_message;
  state.handoffMessage = config.handoff_message;
  state.outOfOfficeMessage = config.out_of_office_message;
  state.resolutionMessage = config.resolution_message;
  state.instructions = config.instructions;
  state.triggerKeywords = config.trigger_keywords || [];
  state.features = {
    conversationFaqs: config.feature_faq || false,
    memories: config.feature_memory || false,
    citations: config.feature_citation || false,
  };
  state.enableHandoff = typeof config.enable_handoff === 'boolean' ? config.enable_handoff : true;
  state.handoffDisabledResponse = config.handoff_disabled_response || '';

  state.temperature = config.temperature || 1;
};

const handleBasicInfoUpdate = async () => {
  const result = await Promise.all([
    v$.value.name.$validate(),
    v$.value.description.$validate(),
    v$.value.productName.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    name: state.name,
    description: state.description,
    config: {
      ...props.assistant.config,
      product_name: state.productName,
    },
  };

  emit('submit', payload);
};

const handleSystemMessagesUpdate = async () => {
  const result = await Promise.all([
    v$.value.welcomeMessage.$validate(),
    v$.value.handoffMessage.$validate(),
    v$.value.outOfOfficeMessage.$validate(),
    v$.value.resolutionMessage.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    config: {
      ...props.assistant.config,
      welcome_message: state.welcomeMessage,
      handoff_message: state.handoffMessage,
      out_of_office_message: state.outOfOfficeMessage,
      resolution_message: state.resolutionMessage,
      enable_handoff: state.enableHandoff,
      handoff_disabled_response: state.handoffDisabledResponse,
    },
  };

  emit('submit', payload);
};

const handleInstructionsUpdate = async () => {
  const result = await v$.value.instructions.$validate();
  if (!result) return;

  const payload = {
    config: {
      ...props.assistant.config,
      temperature: state.temperature || 1,
      instructions: state.instructions,
    },
  };

  emit('submit', payload);
};

const handleFeaturesUpdate = () => {
  const payload = {
    config: {
      ...props.assistant.config,
      feature_faq: state.features.conversationFaqs,
      feature_memory: state.features.memories,
    },
  };

  emit('submit', payload);
};

const newKeyword = ref('');
const keywordInput = ref(null);

const handleKeywordAdd = () => {
  const keyword = newKeyword.value.trim();
  if (keyword && !state.triggerKeywords.includes(keyword)) {
    state.triggerKeywords.push(keyword);
    newKeyword.value = ''; // Clear input after adding
  }
};

const handleBackspace = (e) => {
  if (newKeyword.value === '' && state.triggerKeywords.length > 0) {
    e.preventDefault();
    state.triggerKeywords.pop();
  }
};

const removeKeyword = (keyword) => {
  state.triggerKeywords = state.triggerKeywords.filter(k => k !== keyword);
};

const handleTriggerKeywordsUpdate = async () => {
  const payload = {
    config: {
      ...props.assistant.config,
      trigger_keywords: state.triggerKeywords,
    },
  };

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (props.mode === 'edit' && newAssistant) {
      updateStateFromAssistant(newAssistant);
    }
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Basic Information Section -->
    <Accordion
      :title="t('CAPTAIN.ASSISTANTS.FORM.SECTIONS.BASIC_INFO')"
      is-open
    >
      <div class="flex flex-col gap-4 pt-4">
        <Input
          v-model="state.name"
          :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
          :message="formErrors.name"
          :message-type="formErrors.name ? 'error' : 'info'"
        />

        <Editor
          v-model="state.description"
          :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
          :message="formErrors.description"
          :message-type="formErrors.description ? 'error' : 'info'"
        />

        <Input
          v-model="state.productName"
          :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
          :message="formErrors.productName"
          :message-type="formErrors.productName ? 'error' : 'info'"
        />

        <div class="flex justify-end">
          <Button
            size="small"
            :loading="isLoading"
            @click="handleBasicInfoUpdate"
          >
            {{ t('CAPTAIN.ASSISTANTS.FORM.UPDATE') }}
          </Button>
        </div>
      </div>
    </Accordion>

    <!-- Instructions Section -->
    <Accordion :title="t('CAPTAIN.ASSISTANTS.FORM.SECTIONS.INSTRUCTIONS')">
      <div class="flex flex-col gap-4">
        <Editor
          v-model="state.instructions"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.PLACEHOLDER')"
          :message="formErrors.instructions"
          :max-length="20000"
          :message-type="formErrors.instructions ? 'error' : 'info'"
        />

        <div class="flex flex-col gap-2 mt-4">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.LABEL') }}
          </label>
          <div class="flex items-center gap-4">
            <input
              v-model="state.temperature"
              type="range"
              min="0"
              max="1"
              step="0.1"
              class="w-full"
            />
            <span class="text-sm text-n-slate-12">{{ state.temperature }}</span>
          </div>
          <p class="text-sm text-n-slate-11 italic">
            {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex justify-end">
          <Button
            size="small"
            :loading="isLoading"
            :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
            @click="handleInstructionsUpdate"
          />
        </div>
      </div>
    </Accordion>

    <!-- Greeting Messages Section -->
    <Accordion :title="t('CAPTAIN.ASSISTANTS.FORM.SECTIONS.SYSTEM_MESSAGES')">
      <div class="flex flex-col gap-4 pt-4">
        <Editor

          v-model="state.welcomeMessage"
          :label="t('CAPTAIN.ASSISTANTS.FORM.WELCOME_MESSAGE.LABEL')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.WELCOME_MESSAGE.PLACEHOLDER')"
          :message="formErrors.welcomeMessage"
          :message-type="formErrors.welcomeMessage ? 'error' : 'info'"
        />
        <Editor

          v-model="state.handoffMessage"
          :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.LABEL')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER')
          "
          :message="formErrors.handoffMessage"
          :message-type="formErrors.handoffMessage ? 'error' : 'info'"
        />
        <div class="flex items-center gap-2 mb-2">
          <input
            v-model="state.enableHandoff"
            type="checkbox"
            id="enableHandoffToggle"
            class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label for="enableHandoffToggle" class="text-sm font-medium text-gray-700 dark:text-slate-300">
            {{ t('CAPTAIN.ASSISTANTS.FORM.ENABLE_HANDOFF.LABEL', 'Enable Agent Handoff') }}
          </label>
        </div>
        <Editor
          v-if="!state.enableHandoff"
          v-model="state.handoffDisabledResponse"
          :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_DISABLED_RESPONSE.LABEL', 'Response when Handoff is Disabled')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_DISABLED_RESPONSE.PLACEHOLDER', 'Enter response when handoff is disabled...')"
          :message="formErrors.handoffDisabledResponse"
          :message-type="formErrors.handoffDisabledResponse ? 'error' : 'info'"
        />
        <Editor
          v-model="state.outOfOfficeMessage"
          :label="t('CAPTAIN.ASSISTANTS.FORM.OUT_OF_OFFICE_MESSAGE.LABEL', 'Out of Office Message')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.OUT_OF_OFFICE_MESSAGE.PLACEHOLDER', 'Enter out of office message')"
          :message="formErrors.outOfOfficeMessage"
          :message-type="formErrors.outOfOfficeMessage ? 'error' : 'info'"
        />
        <Editor
          v-model="state.resolutionMessage"
          :label="t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.LABEL')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER')
          "
          :message="formErrors.resolutionMessage"
          :message-type="formErrors.resolutionMessage ? 'error' : 'info'"
        />

        <div class="flex justify-end">
          <Button
            size="small"
            :loading="isLoading"
            :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
            @click="handleSystemMessagesUpdate"
          />
        </div>
      </div>
    </Accordion>

    <!-- Add new Trigger Keywords Section after System Messages -->
    <Accordion :title="t('Trigger Keywords')">
      <div class="flex flex-col gap-4 pt-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.TRIGGER_KEYWORDS.LABEL', 'Keywords') }}
          </label>
          <p class="text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.TRIGGER_KEYWORDS.DESCRIPTION', 'Add keywords that will trigger this assistant to respond. Leave empty to respond to all messages.') }}
          </p>
          
          <!-- Keywords Input -->
          <div class="flex flex-wrap gap-2 p-4 border rounded-lg border-n-slate-6 bg-n-slate-1">
            <div
              v-for="keyword in state.triggerKeywords"
              :key="keyword"
              class="flex items-center gap-1 px-3 py-1.5 text-sm rounded-full bg-n-slate-3 text-n-slate-12"
            >
              <span>{{ keyword }}</span>
              <button
                type="button"
                class="flex items-center justify-center w-5 h-5 ml-1 rounded-full hover:bg-n-slate-4"
                @click="removeKeyword(keyword)"
              >
                <span class="text-xs">&times;</span>
              </button>
            </div>
            
            <input
              ref="keywordInput"
              type="text"
              class="flex-1 min-w-[150px] bg-transparent border-none focus:outline-none text-sm text-n-slate-12 placeholder:text-n-slate-9"
              :placeholder="t('CAPTAIN.ASSISTANTS.FORM.TRIGGER_KEYWORDS.PLACEHOLDER', 'Type a keyword and press Enter')"
              v-model="newKeyword"
              @keydown.enter.prevent="handleKeywordAdd"
              @keydown.backspace="handleBackspace"
            />
          </div>
        </div>

        <div class="flex justify-end">
          <Button
            size="small"
            :loading="isLoading"
            :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
            @click="handleTriggerKeywordsUpdate"
          />
        </div>
      </div>
    </Accordion>

    <!-- Features Section -->
    <Accordion :title="t('CAPTAIN.ASSISTANTS.FORM.SECTIONS.FEATURES')">
      <div class="flex flex-col gap-4 pt-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
          </label>
          <div class="flex flex-col gap-2">
            <label class="flex items-center gap-2">
              <input
                v-model="state.features.conversationFaqs"
                type="checkbox"
              />
              {{
                t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS')
              }}
            </label>
            <label class="flex items-center gap-2">
              <input v-model="state.features.memories" type="checkbox" />
              {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
            </label>
            <label class="flex items-center gap-2">
              <input v-model="state.features.citations" type="checkbox" />
              {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
            </label>
          </div>
        </div>

        <div class="flex justify-end">
          <Button
            size="small"
            :loading="isLoading"
            :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
            @click="handleFeaturesUpdate"
          />
        </div>
      </div>
    </Accordion>
  </form>
</template>
